import React, { useRef } from 'react'
import { useMount } from 'react-use'

import useNotyf from '@/hooks/useNotyf'
import { CUSTOM_EVENT, TOAST_AUTOCLOSE_DELAY } from '@/lib/constants'

// Shows a toast when the API rejects a request for being sent too often (HTTP
// 429). The apiService interceptor fires the "rateLimited" event with the
// number of seconds from the Retry-After header.
const RateLimitedWarning: React.FC = () => {
  const notyf = useNotyf()
  const lastShownAt = useRef(0)

  useMount(() => {
    function onRateLimited(event: Event) {
      // Search-as-you-type can hit the limit several times in a row, so only
      // show one toast while the previous one is still on screen.
      if (Date.now() - lastShownAt.current < TOAST_AUTOCLOSE_DELAY) {
        return
      }

      lastShownAt.current = Date.now()

      const retryAfterSeconds =
        event instanceof CustomEvent ? Number(event.detail) : 0

      notyf.error({
        message: `Too many attempts. Please wait ${formatWaitTime(retryAfterSeconds)} and try again.`,
        duration: 20000,
      })
    }

    window.addEventListener(CUSTOM_EVENT.rateLimited, onRateLimited)

    return () => {
      window.removeEventListener(CUSTOM_EVENT.rateLimited, onRateLimited)
    }
  })

  return null
}

function formatWaitTime(seconds: number): string {
  if (seconds <= 60) {
    return 'a minute'
  }

  return `${Math.ceil(seconds / 60)} minutes`
}

export default RateLimitedWarning
